// Form field wrapper and the input styling every control shares.

import type { ReactNode } from "react";
import { cx } from "@/platform/ui/primitives/cx.js";

// ---- Form fields ----
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-muted">{label}</span>
      {children}
    </label>
  );
}

// Base input styling without a width, so it can be combined with flex/explicit
// widths. `color-scheme:dark` keeps native date/time picker icons light on dark.
export const inputBase =
  "rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-accent [color-scheme:dark] disabled:cursor-not-allowed disabled:border-line/60 disabled:text-dim";
export const inputClass = "w-full " + inputBase;

// A single MM:SS duration input — two fields with a fixed ":" between them, so a
// run time is typed the way it reads. `compact` drops the border/background for
// use inside a tile that already has its own border (e.g. a stat tile).
export function TimeInput({
  min,
  sec,
  setMin,
  setSec,
  compact,
}: {
  min: string;
  sec: string;
  setMin: (v: string) => void;
  setSec: (v: string) => void;
  compact?: boolean;
}) {
  const cell = cx(
    "mono w-full min-w-0 bg-transparent text-center outline-none",
    compact ? "px-0 py-0 text-sm font-bold text-ink-soft" : "px-1 py-2 text-[13px] text-ink-soft",
  );
  return (
    <div
      className={cx(
        "flex items-center",
        compact
          ? "gap-0.5"
          : "rounded-lg border border-line bg-surface-2 focus-within:border-accent",
      )}
    >
      <input
        value={min}
        onChange={(e) => setMin(e.target.value)}
        placeholder="min"
        aria-label="Minutes"
        inputMode="numeric"
        className={cell}
      />
      <span className="mono text-faint">:</span>
      <input
        value={sec}
        onChange={(e) => setSec(e.target.value)}
        placeholder="sec"
        aria-label="Seconds"
        inputMode="numeric"
        className={cell}
      />
    </div>
  );
}
